import type { CollaboratorResponse } from 'external-api-types';
import { RESET_STUDIO_ACTION } from '../consts';
import type { StudioCollaboratorsActions } from '../../actions/studio/collaborators';

export interface State {
    isFetchingCollaborators: boolean;
    entries: CollaboratorResponse[];
    collaboratorsError: string | null;
    collaboratorsState: Record<CollaboratorResponse['id'], { error?: string | null; isUpdating?: boolean }>;
    isInvitingCollaborator: boolean;
    inviteCollaboratorError: string | null;
}

const getDefaultState = (): State => ({
    isFetchingCollaborators: false,
    entries: [],
    collaboratorsError: null,
    collaboratorsState: {},
    isInvitingCollaborator: false,
    inviteCollaboratorError: null
});

const studioCollaboratorsReducer = (state = getDefaultState(), action: StudioCollaboratorsActions | null): State => {
    switch (action?.type) {
        case 'FETCH_COLLABORATORS_STARTED': {
            return {
                ...state,
                isFetchingCollaborators: true
            };
        }

        case 'FETCH_COLLABORATORS_SUCCEED': {
            return {
                ...state,
                entries: action.data.collaborators,
                isFetchingCollaborators: false
            };
        }

        case 'FETCH_COLLABORATORS_FAILED': {
            return {
                ...state,
                collaboratorsError: action.error,
                isFetchingCollaborators: false
            };
        }

        case 'INVITE_COLLABORATOR_STARTED': {
            return {
                ...state,
                isInvitingCollaborator: true
            };
        }

        case 'INVITE_COLLABORATOR_SUCCEED': {
            return {
                ...state,
                isInvitingCollaborator: false
            };
        }

        case 'INVITE_COLLABORATOR_FAILED': {
            return {
                ...state,
                inviteCollaboratorError: action.error,
                isInvitingCollaborator: false
            };
        }

        case 'EDIT_COLLABORATOR_STARTED': {
            const { collaboratorId } = action.data;
            const foundCollaborator = state.entries.find((collaborator) => collaborator.id === collaboratorId);
            return {
                ...state,
                collaboratorsState: {
                    ...state.collaboratorsState,
                    ...(foundCollaborator?.id && {
                        [foundCollaborator?.id]: {
                            ...state.collaboratorsState[foundCollaborator?.id],
                            isUpdating: true
                        }
                    })
                }
            };
        }

        case 'EDIT_COLLABORATOR_SUCCEED': {
            const { collaboratorId } = action.data;
            const foundCollaborator = state.entries.find((collaborator) => collaborator.id === collaboratorId);
            return {
                ...state,
                collaboratorsState: {
                    ...state.collaboratorsState,
                    ...(foundCollaborator?.id && {
                        [foundCollaborator?.id]: {
                            ...state.collaboratorsState[foundCollaborator?.id],
                            isUpdating: false,
                            error: null
                        }
                    })
                }
            };
        }

        case 'EDIT_COLLABORATOR_FAILED': {
            const { collaboratorId, error } = action.data;
            const foundCollaborator = state.entries.find((collaborator) => collaborator.id === collaboratorId);
            return {
                ...state,
                collaboratorsState: {
                    ...state.collaboratorsState,
                    ...(foundCollaborator?.id && {
                        [foundCollaborator?.id]: {
                            ...state.collaboratorsState[foundCollaborator?.id],
                            error,
                            isUpdating: false
                        }
                    })
                }
            };
        }

        case RESET_STUDIO_ACTION: {
            return getDefaultState();
        }

        default: {
            return state;
        }
    }
};

export default studioCollaboratorsReducer;
